import {
  type ConfiguracaoCaptura,
  configuracaoCapturaSchema,
  type DiaSemana,
  minutosDoHorario,
  type TipoAgendaCaptura,
} from '@fiscoai/contracts';

/**
 * O que a pessoa está editando: guarda os campos dos três modos ao mesmo tempo, para trocar de modo
 * e voltar sem perder o que digitou. Só vira `ConfiguracaoCaptura` ao salvar. Por isso não usa
 * React Hook Form: o rascunho não tem o formato do schema, mas é validado por ele
 * (`errosDoRascunho`), o mesmo que o servidor aplica.
 */
export interface RascunhoCaptura {
  ativa: boolean;
  cienciaAutomatica: boolean;
  tipo: TipoAgendaCaptura;
  horarios: string[];
  dias: DiaSemana[];
  inicio: string;
  fim: string;
  intervaloJanela: number;
  intervaloContinuo: number;
}

const DIAS_UTEIS: DiaSemana[] = ['SEG', 'TER', 'QUA', 'QUI', 'SEX'];

export function deConfiguracao(c: ConfiguracaoCaptura): RascunhoCaptura {
  const a = c.agenda;
  return {
    ativa: c.ativa,
    cienciaAutomatica: c.cienciaAutomatica,
    tipo: a.tipo,
    horarios: a.tipo === 'HORARIOS' ? [...a.horarios] : [],
    dias: a.tipo === 'CONTINUA' ? DIAS_UTEIS : [...a.dias],
    inicio: a.tipo === 'JANELA' ? a.inicio : '06:00',
    fim: a.tipo === 'JANELA' ? a.fim : '18:00',
    intervaloJanela: a.tipo === 'JANELA' ? a.intervaloMinutos : 65,
    intervaloContinuo: a.tipo === 'CONTINUA' ? a.intervaloMinutos : 120,
  };
}

const ORDEM_DIAS: readonly DiaSemana[] = ['DOM', 'SEG', 'TER', 'QUA', 'QUI', 'SEX', 'SAB'];
const ordenarDias = (dias: readonly DiaSemana[]) =>
  [...dias].sort((a, b) => ORDEM_DIAS.indexOf(a) - ORDEM_DIAS.indexOf(b));

export function paraConfiguracao(r: RascunhoCaptura): ConfiguracaoCaptura {
  const base = { ativa: r.ativa, cienciaAutomatica: r.cienciaAutomatica };
  if (r.tipo === 'HORARIOS') {
    const horarios = [...r.horarios].sort((a, b) => minutosDoHorario(a) - minutosDoHorario(b));
    return { ...base, agenda: { tipo: 'HORARIOS', horarios, dias: ordenarDias(r.dias) } };
  }
  if (r.tipo === 'JANELA')
    return {
      ...base,
      agenda: {
        tipo: 'JANELA',
        inicio: r.inicio,
        fim: r.fim,
        intervaloMinutos: r.intervaloJanela,
        dias: ordenarDias(r.dias),
      },
    };
  return { ...base, agenda: { tipo: 'CONTINUA', intervaloMinutos: r.intervaloContinuo } };
}

export type CampoAgenda = 'horarios' | 'dias' | 'inicio' | 'fim' | 'intervaloMinutos';

/** Valida pelo mesmo schema do servidor e devolve a primeira mensagem de cada campo. */
export function errosDoRascunho(r: RascunhoCaptura): Partial<Record<CampoAgenda, string>> {
  const resultado = configuracaoCapturaSchema.safeParse(paraConfiguracao(r));
  if (resultado.success) return {};
  const erros: Partial<Record<CampoAgenda, string>> = {};
  for (const issue of resultado.error.issues) {
    const campo = issue.path[1];
    if (typeof campo === 'string' && !(campo in erros)) erros[campo as CampoAgenda] = issue.message;
  }
  return erros;
}
